import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetProductsQuery,
  useDeleteProductMutation,
  useSearchProductsQuery,
  usePreviewProductImportMutation,
  useConfirmProductImportMutation,
  usePreviewWebshopSyncMutation,
  useConfirmWebshopSyncMutation
} from "../../redux/api/productsApi";
import { useGetManufacturersQuery } from "../../redux/api/manufacturersApi";
import { useGetLanguagesQuery } from "../../redux/api/languagesApi";
import { getImageUrl } from "../../utils/imageUtils";
import { formatPrice } from "../../utils/currencyUtils";
import { 
  Pagination, 
  AutoComplete, 
  Input, 
  Select, 
  Table, 
  Button, 
  Tag, 
  Typography, 
  Alert, 
  Space,
  Modal,
  Image,
  Checkbox,
  Progress
} from "antd";
import { SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

const { Title, Text } = Typography;

const ProductsPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importManufacturerName, setImportManufacturerName] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importPreviewProducts, setImportPreviewProducts] = useState([]);
  const [importPreviewError, setImportPreviewError] = useState(null);
  const [isImportRunning, setIsImportRunning] = useState(false);
  const [importCurrentTitle, setImportCurrentTitle] = useState("");
  const [importProgressCurrent, setImportProgressCurrent] = useState(0);
  const [importProgressTotal, setImportProgressTotal] = useState(0);
  const [importRunSummary, setImportRunSummary] = useState(null);
  const [isWebshopSyncModalOpen, setIsWebshopSyncModalOpen] = useState(false);
  const [webshopSyncPublished, setWebshopSyncPublished] = useState(0);
  const [webshopSyncAppendManufacturerName, setWebshopSyncAppendManufacturerName] = useState(false);
  const [webshopSyncLanguageCodes, setWebshopSyncLanguageCodes] = useState(["cs", "sk"]);
  const [webshopSyncPreview, setWebshopSyncPreview] = useState(null);
  const [webshopSyncTranslations, setWebshopSyncTranslations] = useState([]);
  const [webshopSyncError, setWebshopSyncError] = useState(null);
  const [isWebshopSyncRunning, setIsWebshopSyncRunning] = useState(false);
  const [webshopSyncCurrentTitle, setWebshopSyncCurrentTitle] = useState("");
  const [webshopSyncProgressCurrent, setWebshopSyncProgressCurrent] = useState(0);
  const [webshopSyncProgressTotal, setWebshopSyncProgressTotal] = useState(0);
  const [webshopSyncRunSummary, setWebshopSyncRunSummary] = useState(null);

  const { data, isLoading, error } = useGetProductsQuery({
    showDeleted: false,
    page: currentPage,
    limit: itemsPerPage,
    manufacturerId: selectedManufacturer
  });

  // Get manufacturers for filter
  const { data: manufacturers = [], isLoading: manufacturersLoading } = useGetManufacturersQuery();
  const { data: languages = [] } = useGetLanguagesQuery();

  // Search products query - allow 1 character for numeric searches (product ID), 2 for text searches
  const isNumericSearch = /^\d+$/.test(searchQuery);
  const minSearchLength = isNumericSearch ? 1 : 2;
  const { data: searchData, isLoading: isSearching } = useSearchProductsQuery(
    { search: searchQuery, limit: 20 },
    { skip: searchQuery.length < minSearchLength }
  );
  
  const [deleteProduct] = useDeleteProductMutation();
  const [previewProductImport, { isLoading: isPreviewingImport }] = usePreviewProductImportMutation();
  const [confirmProductImport] = useConfirmProductImportMutation();
  const [previewWebshopSync, { isLoading: isPreviewingWebshopSync }] = usePreviewWebshopSyncMutation();
  const [confirmWebshopSync] = useConfirmWebshopSyncMutation();

  const products = data?.products || [];
  const pagination = data?.pagination || { current_page: 1, total_pages: 1, total: 0, per_page: 10 };

  const handleDelete = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (productToDelete) {
      try {
        await deleteProduct(productToDelete.product_id).unwrap();
        toast.success("Product deleted successfully!");
        setShowDeleteModal(false);
        setProductToDelete(null);
      } catch (err) {
        toast.error(err?.data?.message || err?.error || "Failed to delete product");
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleManufacturerFilterChange = (value) => {
    setSelectedManufacturer(value);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const resetImportState = () => {
    setImportFile(null);
    setImportManufacturerName("");
    setImportPreview(null);
    setImportPreviewProducts([]);
    setImportPreviewError(null);
    setIsImportRunning(false);
    setImportCurrentTitle("");
    setImportProgressCurrent(0);
    setImportProgressTotal(0);
    setImportRunSummary(null);
  };

  const handleOpenImportModal = () => {
    resetImportState();
    setIsImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    if (isImportRunning) {
      return;
    }
    setIsImportModalOpen(false);
    resetImportState();
  };

  const handlePreviewImport = async () => {
    if (!importFile) {
      toast.error("Please select a JSON file to preview.");
      return;
    }

    try {
      setImportPreviewError(null);
      const formData = new FormData();
      formData.append("file", importFile);
      if (importManufacturerName.trim()) {
        formData.append("manufacturer_name", importManufacturerName.trim());
      }

      const result = await previewProductImport(formData).unwrap();
      const previewProducts = (result.products || []).map((product) => ({
        ...product,
        accepted: product.accepted !== false,
      }));

      setImportPreview(result);
      setImportManufacturerName(result.manufacturer_name || importManufacturerName);
      setImportPreviewProducts(previewProducts);
      setImportRunSummary(null);
      toast.success(`Preview ready: ${previewProducts.length} product(s) found.`);
    } catch (err) {
      const message = err?.data?.error || err?.error || "Failed to preview import";
      setImportPreviewError(message);
      toast.error(message);
    }
  };

  const handleToggleImportProduct = (previewId, accepted) => {
    setImportPreviewProducts((prev) =>
      prev.map((product) =>
        product.preview_id === previewId ? { ...product, accepted } : product
      )
    );
  };

  const handleToggleAllImportProducts = (accepted) => {
    setImportPreviewProducts((prev) => prev.map((product) => ({ ...product, accepted })));
  };

  const handleConfirmImport = async () => {
    const acceptedProducts = importPreviewProducts.filter((product) => product.accepted);
    if (acceptedProducts.length === 0) {
      toast.error("Select at least one product to import.");
      return;
    }

    try {
      setIsImportRunning(true);
      setImportCurrentTitle("");
      setImportProgressCurrent(0);
      setImportProgressTotal(acceptedProducts.length);

      const aggregateSummary = {
        total: acceptedProducts.length,
        imported: 0,
        updated: 0,
        skipped: 0,
        failed: 0,
      };
      setImportRunSummary(aggregateSummary);

      for (let index = 0; index < acceptedProducts.length; index += 1) {
        const product = acceptedProducts[index];
        setImportCurrentTitle(product.title);

        try {
          const result = await confirmProductImport({
            source_name: importPreview?.source_name,
            manufacturer_name: importManufacturerName.trim() || importPreview?.manufacturer_name,
            products: [{ ...product, accepted: true }],
          }).unwrap();

          const productResult = result?.products?.[0];
          const status = productResult?.status || "updated";
          if (aggregateSummary[status] !== undefined) {
            aggregateSummary[status] += 1;
          }

          setImportPreviewProducts((prev) =>
            prev.map((item) =>
              item.preview_id === product.preview_id
                ? {
                    ...item,
                    import_status: status,
                    import_error: productResult?.error || null,
                    product_id: productResult?.product_id || item.product_id,
                  }
                : item
            )
          );
        } catch (err) {
          aggregateSummary.failed += 1;
          const errorMessage = err?.data?.error || err?.error || "Failed to import product";

          setImportPreviewProducts((prev) =>
            prev.map((item) =>
              item.preview_id === product.preview_id
                ? {
                    ...item,
                    import_status: "failed",
                    import_error: errorMessage,
                  }
                : item
            )
          );
        }

        setImportProgressCurrent(index + 1);
        setImportRunSummary({ ...aggregateSummary });
      }

      setImportCurrentTitle("");
      const successMessage = `Import finished: ${aggregateSummary.imported || 0} imported, ${aggregateSummary.updated || 0} updated, ${aggregateSummary.skipped || 0} skipped, ${aggregateSummary.failed || 0} failed.`;
      if (aggregateSummary.failed > 0) {
        toast.error(successMessage);
      } else {
        toast.success(successMessage);
      }
    } catch (err) {
      toast.error(err?.data?.error || err?.error || "Failed to confirm import");
    } finally {
      setIsImportRunning(false);
    }
  };

  const resetWebshopSyncState = () => {
    setWebshopSyncPublished(0);
    setWebshopSyncAppendManufacturerName(false);
    setWebshopSyncLanguageCodes(["cs", "sk"]);
    setWebshopSyncPreview(null);
    setWebshopSyncTranslations([]);
    setWebshopSyncError(null);
    setIsWebshopSyncRunning(false);
    setWebshopSyncCurrentTitle("");
    setWebshopSyncProgressCurrent(0);
    setWebshopSyncProgressTotal(0);
    setWebshopSyncRunSummary(null);
  };

  const handleOpenWebshopSyncModal = () => {
    resetWebshopSyncState();
    setIsWebshopSyncModalOpen(true);
  };

  const handleCloseWebshopSyncModal = () => {
    if (isWebshopSyncRunning) {
      return;
    }

    setIsWebshopSyncModalOpen(false);
    resetWebshopSyncState();
  };

  const handlePreviewWebshopSync = async () => {
    try {
      setWebshopSyncError(null);
      const result = await previewWebshopSync({
        published: webshopSyncPublished,
        append_manufacturer_name: webshopSyncAppendManufacturerName ? 1 : 0,
        language_codes: webshopSyncLanguageCodes,
        manufacturer_id: selectedManufacturer,
      }).unwrap();

      const previewTranslations = (result.translations || []).map((translation) => ({
        ...translation,
        accepted: translation.accepted !== false && translation.syncable !== false,
      }));

      setWebshopSyncPreview(result);
      setWebshopSyncTranslations(previewTranslations);
      setWebshopSyncRunSummary(null);
      toast.success(`Preview ready: ${previewTranslations.length} translation(s) found.`);
    } catch (err) {
      const message = err?.data?.error || err?.error || "Failed to preview webshop sync";
      setWebshopSyncError(message);
      toast.error(message);
    }
  };

  const handleToggleWebshopTranslation = (translationId, accepted) => {
    setWebshopSyncTranslations((prev) =>
      prev.map((translation) =>
        translation.translation_id === translationId && translation.syncable !== false
          ? { ...translation, accepted }
          : translation
      )
    );
  };

  const handleToggleAllWebshopTranslations = (accepted) => {
    setWebshopSyncTranslations((prev) =>
      prev.map((translation) =>
        translation.syncable !== false ? { ...translation, accepted } : translation
      )
    );
  };

  const handleConfirmWebshopSync = async () => {
    const acceptedTranslations = webshopSyncTranslations.filter((translation) => translation.accepted);
    if (acceptedTranslations.length === 0) {
      toast.error("Select at least one translation to sync.");
      return;
    }

    try {
      setIsWebshopSyncRunning(true);
      setWebshopSyncCurrentTitle("");
      setWebshopSyncProgressCurrent(0);
      setWebshopSyncProgressTotal(acceptedTranslations.length);

      const aggregateSummary = {
        total: acceptedTranslations.length,
        synced: 0,
        skipped: 0,
        failed: 0,
      };
      setWebshopSyncRunSummary(aggregateSummary);

      for (let index = 0; index < acceptedTranslations.length; index += 1) {
        const translation = acceptedTranslations[index];
        setWebshopSyncCurrentTitle(`${translation.title} (${translation.language_code})`);

        try {
          const result = await confirmWebshopSync({
            published: webshopSyncPublished,
            append_manufacturer_name: webshopSyncAppendManufacturerName ? 1 : 0,
            translations: [{ translation_id: translation.translation_id, accepted: true }],
          }).unwrap();

          const translationResult = result?.translations?.[0];
          const status = translationResult?.status || "synced";
          if (aggregateSummary[status] !== undefined) {
            aggregateSummary[status] += 1;
          }

          setWebshopSyncTranslations((prev) =>
            prev.map((item) =>
              item.translation_id === translation.translation_id
                ? {
                    ...item,
                    sync_status: status,
                    sync_error: translationResult?.error || null,
                    web_id: translationResult?.item_id || item.web_id,
                  }
                : item
            )
          );
        } catch (err) {
          aggregateSummary.failed += 1;
          const errorMessage = err?.data?.error || err?.error || "Failed to sync translation";

          setWebshopSyncTranslations((prev) =>
            prev.map((item) =>
              item.translation_id === translation.translation_id
                ? {
                    ...item,
                    sync_status: "failed",
                    sync_error: errorMessage,
                  }
                : item
            )
          );
        }

        setWebshopSyncProgressCurrent(index + 1);
        setWebshopSyncRunSummary({ ...aggregateSummary });
      }

      setWebshopSyncCurrentTitle("");
      const successMessage = `Webshop sync finished: ${aggregateSummary.synced || 0} synced, ${aggregateSummary.skipped || 0} skipped, ${aggregateSummary.failed || 0} failed.`;
      if (aggregateSummary.failed > 0) {
        toast.error(successMessage);
      } else {
        toast.success(successMessage);
      }
    } catch (err) {
      toast.error(err?.data?.error || err?.error || "Failed to confirm webshop sync");
    } finally {
      setIsWebshopSyncRunning(false);
    }
  };

  // Generate AutoComplete options from search results
  const webshopLanguageOptions = useMemo(() => {
    const supportedCodes = new Set(["cs", "sk"]);
    return languages
      .filter((language) => supportedCodes.has(language.code))
      .map((language) => ({
        label: `${language.name} (${language.code})`,
        value: language.code,
      }));
  }, [languages]);

  const searchOptions = useMemo(() => {
    const isNumericSearch = /^\d+$/.test(searchQuery);
    const minSearchLength = isNumericSearch ? 1 : 2;
    if (!searchData?.products || searchQuery.length < minSearchLength) {
      return [];
    }
    return searchData.products.map((product) => ({
      value: `${product.product_id} - ${product.name}`,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {product.primary_image_url && (
            <img
              src={getImageUrl(product.primary_image_url)}
              alt={product.name}
              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
            />
          )}
          <div>
            <div style={{ fontWeight: 500 }}>#{product.product_id} - {product.name}</div>
            {product.manufacturer_name && (
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{product.manufacturer_name}</div>
            )}
            {product.base_price && (
              <div style={{ fontSize: '12px', color: '#8c8c8c' }}>{formatPrice(product.base_price, 'CZK')}</div>
            )}
          </div>
        </div>
      ),
      product: product,
    }));
  }, [searchData, searchQuery]);

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const handleSelect = (value, option) => {
    if (option.product) {
      navigate(`/products/${option.product.product_id}`);
      setSearchQuery("");
    }
  };

  // Table columns configuration
  const columns = [
    {
      title: 'Image',
      key: 'image',
      width: '10%',
      render: (_, record) => (
        record.primary_image_url ? (
          <Image
            src={getImageUrl(record.primary_image_url)}
            alt={record.name}
            style={{ height: '70px', objectFit: 'cover', borderRadius: '4px' }}
            preview={false}
          />
        ) : (
          <Text type="secondary">No image</Text>
        )
      ),
    },
    {
      title: 'ID',
      dataIndex: 'product_id',
      key: 'product_id',
      width: '8%',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '20%',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Manufacturer',
      dataIndex: 'manufacturer_name',
      key: 'manufacturer_name',
      width: '15%',
    },
    {
      title: 'Price',
      dataIndex: 'base_price',
      key: 'base_price',
      width: '12%',
      render: (price) => formatPrice(price, 'CZK'),
    },
    {
      title: 'Languages',
      dataIndex: 'language_codes',
      key: 'language_codes',
      width: '15%',
      render: (codes) => (
        codes ? (
          <Space size="small" wrap>
            {codes.split(', ').map((code, index) => (
              <Tag key={index} color="cyan">{code}</Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">No translations</Text>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: '10%',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'error'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '10%',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/products/${record.product_id}`)}
            title="View"
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/products/${record.product_id}/edit`)}
            title="Edit"
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            title="Delete"
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const importColumns = [
    {
      title: (
        <Checkbox
          checked={importPreviewProducts.length > 0 && importPreviewProducts.every((product) => product.accepted)}
          indeterminate={importPreviewProducts.some((product) => product.accepted) && !importPreviewProducts.every((product) => product.accepted)}
          onChange={(e) => handleToggleAllImportProducts(e.target.checked)}
        />
      ),
      key: 'accepted',
      width: 60,
      render: (_, record) => (
        <Checkbox
          checked={record.accepted}
          onChange={(e) => handleToggleImportProduct(record.preview_id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 180,
    },
    {
      title: 'Images',
      dataIndex: 'image_count',
      key: 'image_count',
      width: 100,
    },
    {
      title: 'PDF',
      dataIndex: 'pdf_url',
      key: 'pdf_url',
      width: 100,
      render: (pdfUrl) => (pdfUrl ? <Tag color="blue">Yes</Tag> : <Text type="secondary">No</Text>),
    },
    {
      title: 'Cleanup',
      key: 'cleanup',
      width: 180,
      render: (_, record) => (
        <Space size="small" wrap>
          {record.logo_images_removed > 0 && <Tag color="gold">{record.logo_images_removed} logos</Tag>}
          {record.duplicate_images_removed > 0 && <Tag color="purple">{record.duplicate_images_removed} duplicates</Tag>}
        </Space>
      ),
    },
    {
      title: 'Import Status',
      key: 'import_status',
      width: 140,
      render: (_, record) => {
        const status = record.import_status;
        if (!status) {
          return <Text type="secondary">Pending</Text>;
        }

        const colorMap = {
          imported: 'success',
          updated: 'processing',
          skipped: 'default',
          failed: 'error',
        };

        return (
          <Space direction="vertical" size={0}>
            <Tag color={colorMap[status] || 'default'}>{status}</Tag>
            {record.import_error ? <Text type="danger">{record.import_error}</Text> : null}
          </Space>
        );
      },
    },
  ];

  const selectableWebshopSyncTranslations = webshopSyncTranslations.filter((translation) => translation.syncable);

  const webshopSyncColumns = [
    {
      title: (
        <Checkbox
          checked={
            selectableWebshopSyncTranslations.length > 0 &&
            selectableWebshopSyncTranslations.every((translation) => translation.accepted)
          }
          indeterminate={
            selectableWebshopSyncTranslations.some((translation) => translation.accepted) &&
            !selectableWebshopSyncTranslations.every((translation) => translation.accepted)
          }
          onChange={(e) => handleToggleAllWebshopTranslations(e.target.checked)}
          disabled={selectableWebshopSyncTranslations.length === 0}
        />
      ),
      key: "accepted",
      width: 60,
      render: (_, record) => (
        <Checkbox
          checked={record.accepted}
          disabled={!record.syncable}
          onChange={(e) => handleToggleWebshopTranslation(record.translation_id, e.target.checked)}
        />
      ),
    },
    {
      title: "Product",
      key: "product",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.title}</Text>
          {record.webshop_title && record.webshop_title !== record.title ? (
            <Text type="secondary">{record.webshop_title}</Text>
          ) : null}
          <Text type="secondary">#{record.product_id}</Text>
        </Space>
      ),
    },
    {
      title: "Language",
      dataIndex: "language_code",
      key: "language_code",
      width: 120,
      render: (languageCode, record) => (
        <Tag color="cyan">{record.language_name || languageCode}</Tag>
      ),
    },
    {
      title: "Manufacturer",
      dataIndex: "manufacturer_name",
      key: "manufacturer_name",
      width: 180,
    },
    {
      title: "Images",
      dataIndex: "image_count",
      key: "image_count",
      width: 90,
    },
    {
      title: "PDFs",
      dataIndex: "pdf_count",
      key: "pdf_count",
      width: 90,
    },
    {
      title: "Preview",
      key: "preview_status",
      width: 220,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.syncable ? "success" : "warning"}>
            {record.syncable ? "Ready" : "Blocked"}
          </Tag>
          {record.blocked_reason ? <Text type="danger">{record.blocked_reason}</Text> : null}
        </Space>
      ),
    },
    {
      title: "Sync Status",
      key: "sync_status",
      width: 180,
      render: (_, record) => {
        if (!record.sync_status) {
          return <Text type="secondary">Pending</Text>;
        }

        const colorMap = {
          synced: "success",
          skipped: "default",
          failed: "error",
        };

        return (
          <Space direction="vertical" size={0}>
            <Tag color={colorMap[record.sync_status] || "default"}>{record.sync_status}</Tag>
            {record.sync_error ? <Text type="danger">{record.sync_error}</Text> : null}
            {record.web_id ? <Text type="secondary">Item #{record.web_id}</Text> : null}
          </Space>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <Alert title="Loading products..." type="info" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading products"
        description={error?.data?.message || error?.error || 'Failed to load products'}
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={1} style={{ margin: 0 }}>Products</Title>
        <Space>
          <Button onClick={handleOpenImportModal}>
            Import JSON
          </Button>
          <Button onClick={handleOpenWebshopSyncModal}>
            Sync to webshop
          </Button>
          <Button
            type="primary"
            onClick={() => navigate("/products/add")}
          >
            Add Product
          </Button>
        </Space>
      </div>

      {/* Search, Filter, and Items per page selector */}
      <Space orientation="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
        <Space wrap style={{ width: '100%' }}>
          <AutoComplete
            style={{ width: 400, minWidth: 250 }}
            options={searchOptions}
            onSearch={handleSearch}
            onSelect={handleSelect}
            value={searchQuery}
            placeholder="Search by name, description, or ID..."
            allowClear
            notFoundContent={
              isSearching 
                ? "Searching..." 
                : searchQuery.length >= (/^\d+$/.test(searchQuery) ? 1 : 2) 
                  ? "No products found" 
                  : /^\d+$/.test(searchQuery)
                    ? "Type at least 1 digit to search by ID" 
                    : "Type at least 2 characters to search"
            }
          >
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by name, description, or ID..."
              size="large"
            />
          </AutoComplete>
          <Select
            style={{ width: 200 }}
            placeholder="Filter by manufacturer"
            allowClear
            value={selectedManufacturer}
            onChange={handleManufacturerFilterChange}
            loading={manufacturersLoading}
            size="large"
          >
            {manufacturers.map((manufacturer) => (
              <Select.Option key={manufacturer.manufacturer_id} value={manufacturer.manufacturer_id}>
                {manufacturer.name}
              </Select.Option>
            ))}
          </Select>
          <Space>
            <Text type="secondary">Show:</Text>
            <Select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              style={{ width: 80 }}
              size="large"
            >
              <Select.Option value={5}>5</Select.Option>
              <Select.Option value={10}>10</Select.Option>
              <Select.Option value={25}>25</Select.Option>
              <Select.Option value={50}>50</Select.Option>
            </Select>
            <Text type="secondary">products per page</Text>
          </Space>
        </Space>
      </Space>

      {products.length === 0 ? (
        <Alert
          message="No products found"
          description={
            searchQuery || selectedManufacturer 
              ? 'No products match your filters.' 
              : 'No products available.'
          }
          type="info"
          action={
            <Button size="small" onClick={() => navigate("/products/add")}>
              Add your first product
            </Button>
          }
          showIcon
        />
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={products}
            rowKey="product_id"
            pagination={false}
            size="middle"
            rowClassName={(record) => (!record.is_active ? 'ant-table-row-disabled' : '')}
          />

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div style={{ marginTop: 16 }}>
              <Pagination
                current={pagination.current_page}
                total={pagination.total}
                pageSize={itemsPerPage}
                onChange={handlePageChange}
                hideOnSinglePage={true}
                showSizeChanger={false}
              />
            </div>
          )}

          {/* Delete Confirmation Modal */}
          <Modal
            title="Confirm Delete"
            open={showDeleteModal}
            onOk={handleConfirmDelete}
            onCancel={handleCancelDelete}
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <div style={{ textAlign: 'center' }}>
              <p>Are you sure you want to delete the product:</p>
              <Title level={4} style={{ marginBottom: 16 }}>{productToDelete?.name}</Title>
              {productToDelete?.primary_image_url && (
                <Image
                  src={getImageUrl(productToDelete.primary_image_url)}
                  alt="Primary"
                  style={{ height: "100px", objectFit: "cover", borderRadius: "4px" }}
                  preview={false}
                />
              )}
            </div>
          </Modal>

          <Modal
            title="Import Manufacturer JSON"
            open={isImportModalOpen}
            onCancel={handleCloseImportModal}
            footer={[
              <Button key="cancel" onClick={handleCloseImportModal} disabled={isImportRunning}>
                Cancel
              </Button>,
              <Button
                key="preview"
                onClick={handlePreviewImport}
                loading={isPreviewingImport}
                disabled={isImportRunning}
              >
                Preview Import
              </Button>,
              <Button
                key="confirm"
                type="primary"
                onClick={handleConfirmImport}
                disabled={!importPreview || importPreviewProducts.length === 0}
                loading={isImportRunning}
              >
                Confirm Import
              </Button>,
            ]}
            width={1000}
            closable={!isImportRunning}
            maskClosable={!isImportRunning}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div>
                <Text strong>JSON file</Text>
                <input
                  type="file"
                  accept=".json,application/json"
                  className="form-control mt-2"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                />
              </div>

              <div>
                <Text strong>Manufacturer name override</Text>
                <Input
                  className="mt-2"
                  placeholder="Leave blank to derive from JSON source"
                  value={importManufacturerName}
                  onChange={(e) => setImportManufacturerName(e.target.value)}
                />
              </div>

              {importPreviewError && (
                <Alert
                  type="error"
                  message="Preview failed"
                  description={importPreviewError}
                  showIcon
                />
              )}

              {importPreview && (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message={`Source: ${importPreview.source_name} | Manufacturer: ${importManufacturerName || importPreview.manufacturer_name}`}
                    description={`Preview contains ${importPreview.product_count || importPreviewProducts.length} product(s). Uncheck any product you do not want to import.`}
                  />

                  {importProgressTotal > 0 && (
                    <div>
                      <Progress
                        percent={Math.round((importProgressCurrent / importProgressTotal) * 100)}
                        status={isImportRunning ? "active" : (importRunSummary?.failed > 0 ? "exception" : "success")}
                      />
                      <Text type="secondary">
                        {isImportRunning
                          ? `Processing ${importProgressCurrent} / ${importProgressTotal}${importCurrentTitle ? `: ${importCurrentTitle}` : ""}`
                          : `Processed ${importProgressCurrent} / ${importProgressTotal}`}
                      </Text>
                      {importRunSummary && (
                        <div style={{ marginTop: 8 }}>
                          <Space size="small" wrap>
                            <Tag color="success">Imported: {importRunSummary.imported || 0}</Tag>
                            <Tag color="processing">Updated: {importRunSummary.updated || 0}</Tag>
                            <Tag>Skipped: {importRunSummary.skipped || 0}</Tag>
                            <Tag color="error">Failed: {importRunSummary.failed || 0}</Tag>
                          </Space>
                        </div>
                      )}
                    </div>
                  )}

                  <Table
                    columns={importColumns}
                    dataSource={importPreviewProducts}
                    rowKey="preview_id"
                    pagination={false}
                    size="small"
                    scroll={{ y: 360 }}
                  />
                </>
              )}
            </Space>
          </Modal>

          <Modal
            title="Sync Translations To Webshop"
            open={isWebshopSyncModalOpen}
            onCancel={handleCloseWebshopSyncModal}
            footer={[
              <Button key="cancel" onClick={handleCloseWebshopSyncModal} disabled={isWebshopSyncRunning}>
                Cancel
              </Button>,
              <Button
                key="preview"
                onClick={handlePreviewWebshopSync}
                loading={isPreviewingWebshopSync}
                disabled={isWebshopSyncRunning}
              >
                Preview Sync
              </Button>,
              <Button
                key="confirm"
                type="primary"
                onClick={handleConfirmWebshopSync}
                disabled={!webshopSyncPreview || webshopSyncTranslations.length === 0}
                loading={isWebshopSyncRunning}
              >
                Confirm Sync
              </Button>,
            ]}
            width={1100}
            closable={!isWebshopSyncRunning}
            maskClosable={!isWebshopSyncRunning}
          >
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Space wrap style={{ width: "100%" }}>
                <div style={{ minWidth: 220 }}>
                  <Text strong>Published</Text>
                  <Select
                    className="mt-2"
                    value={webshopSyncPublished}
                    onChange={setWebshopSyncPublished}
                    style={{ width: "100%" }}
                    disabled={isWebshopSyncRunning}
                  >
                    <Select.Option value={0}>Unpublished</Select.Option>
                    <Select.Option value={1}>Published</Select.Option>
                  </Select>
                </div>

                <div style={{ minWidth: 280 }}>
                  <Text strong>Languages</Text>
                  <Select
                    mode="multiple"
                    className="mt-2"
                    value={webshopSyncLanguageCodes}
                    onChange={setWebshopSyncLanguageCodes}
                    options={webshopLanguageOptions}
                    style={{ width: "100%" }}
                    placeholder="Select languages"
                    disabled={isWebshopSyncRunning}
                  />
                </div>
              </Space>

              <Checkbox
                checked={webshopSyncAppendManufacturerName}
                onChange={(e) => setWebshopSyncAppendManufacturerName(e.target.checked)}
                disabled={isWebshopSyncRunning}
              >
                Append manufacturer name to webshop title
              </Checkbox>

              {webshopSyncError && (
                <Alert
                  type="error"
                  message="Preview failed"
                  description={webshopSyncError}
                  showIcon
                />
              )}

              {webshopSyncPreview && (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message={`Preview contains ${webshopSyncPreview.summary?.total || webshopSyncTranslations.length} translation(s) for sync.`}
                    description={`Ready: ${webshopSyncPreview.summary?.syncable || 0} | Blocked: ${webshopSyncPreview.summary?.blocked || 0} | New webshop items will be created as ${webshopSyncPublished ? "published" : "unpublished"} with titles ${webshopSyncAppendManufacturerName ? '"Item Name - Manufacturer Name"' : '"Item Name"'}.`}
                  />

                  {webshopSyncProgressTotal > 0 && (
                    <div>
                      <Progress
                        percent={Math.round((webshopSyncProgressCurrent / webshopSyncProgressTotal) * 100)}
                        status={isWebshopSyncRunning ? "active" : (webshopSyncRunSummary?.failed > 0 ? "exception" : "success")}
                      />
                      <Text type="secondary">
                        {isWebshopSyncRunning
                          ? `Processing ${webshopSyncProgressCurrent} / ${webshopSyncProgressTotal}${webshopSyncCurrentTitle ? `: ${webshopSyncCurrentTitle}` : ""}`
                          : `Processed ${webshopSyncProgressCurrent} / ${webshopSyncProgressTotal}`}
                      </Text>
                      {webshopSyncRunSummary && (
                        <div style={{ marginTop: 8 }}>
                          <Space size="small" wrap>
                            <Tag color="success">Synced: {webshopSyncRunSummary.synced || 0}</Tag>
                            <Tag>Skipped: {webshopSyncRunSummary.skipped || 0}</Tag>
                            <Tag color="error">Failed: {webshopSyncRunSummary.failed || 0}</Tag>
                          </Space>
                        </div>
                      )}
                    </div>
                  )}

                  <Table
                    columns={webshopSyncColumns}
                    dataSource={webshopSyncTranslations}
                    rowKey="translation_id"
                    pagination={false}
                    size="small"
                    scroll={{ y: 360 }}
                  />
                </>
              )}
            </Space>
          </Modal>
        </>
      )}
    </div>
  );
};

export default ProductsPage;
